import { motion } from 'framer-motion';
import { useCallback, useMemo } from 'react';
import { useTranscricao } from '../../context/TranscricaoContext';
import useParticipantsWithHost from '../../hooks/useParticipantsWithHost';
import { usePhraseCompletion } from '../../hooks/usePhraseCompletion';
import useWSMessageSender from '../../hooks/useWSMessageSender';
import useWSSlideData from '../../hooks/useWSSlideData';
import useWSTurnState, { TURN_ACTIONS, TURN_PHASES } from '../../hooks/useWSTurnState';
import { extractContinuation } from '../../utils/extractContinuation';
import { areAllPrefixedWordsCorrect } from '../../utils/phraseBusinessLogic';
import ParticipantCamera from '../Slides/ParticipantCamera';
import './PhraseCompletionSection.css';
import './PhraseComponentsCommon.css';
import { PlayPhraseButton } from './PlayPhraseButton';
import SmartCompletionCountdown from './SmartCompletionCountdown';
import { SpokenContinuationContainer } from './SpokenContinuationContainer';
import { ToCompleteWordsContainer } from './ToCompleteWordsContainer';

const MIN_CONTINUATION_WORDS = 5;

// Placeholder de slideData quando não há dados do backend
const PLACEHOLDER_SLIDE_DATA = {
  type: 'frase-completar',
  phraseId: 'placeholder',
  content: [
    { block: 'On' },
    { block: 'Mondays' },
    { block: 'I like to' }
  ]
};

export default function PhraseCompletionSection({ participant }) {
  const { slideData: realSlideData } = useWSSlideData();
  const { transcriptionHistory } = useTranscricao();
  const { currentPhase, isMyTurn } = useWSTurnState();
  const { whoAmI } = useParticipantsWithHost();
  const { sendTurnAction } = useWSMessageSender();

  // Usa slideData real ou placeholder se não houver dados
  const slideData = realSlideData?.content && realSlideData.content.length > 0
    ? realSlideData
    : PLACEHOLDER_SLIDE_DATA;

  // Hook centralizado com toda lógica de frase
  const { blocksWithStatus, allWordStatuses, words, playFullPhrase } = usePhraseCompletion(slideData, isMyTurn);

  // Todas as palavras prefixadas foram pronunciadas corretamente
  const allPrefixedWordsCorrect = areAllPrefixedWordsCorrect(allWordStatuses);

  // Continuação da frase que foi falada
  const spokenContinuation = useMemo(() =>
    allPrefixedWordsCorrect ? extractContinuation(transcriptionHistory, words) : "",
    [allPrefixedWordsCorrect, transcriptionHistory, words]
  );

  // Calcula porcentagem de palavras faladas na continuação
  const continuationProgress = useMemo(() => {
    if (!allPrefixedWordsCorrect || !spokenContinuation) return 0;
    const spokenWords = spokenContinuation.split(' ').length;
    return (spokenWords / MIN_CONTINUATION_WORDS) * 100;
  }, [allPrefixedWordsCorrect, spokenContinuation]);

  const handleAutoComplete = useCallback(() => {
    if (!whoAmI) return;

    let action = null;
    if (whoAmI === 'A') action = TURN_ACTIONS.ACompleted;
    if (whoAmI === 'B') action = TURN_ACTIONS.BCompleted;

    if (!action) return;

    sendTurnAction({
      action,
    });
  }, [whoAmI, sendTurnAction, spokenContinuation]);

  // Animação de pulinho quando entra em A_COMPLETING ou B_COMPLETING
  const shouldJump = currentPhase === TURN_PHASES.ACompleting || currentPhase === TURN_PHASES.BCompleting;

  // Não mostra SmartCompletionCountdown durante A_COMPLETING ou B_COMPLETING
  // porque o NextIn já está mostrando o countdown do backend
  const shouldShowSmartCountdown = isMyTurn &&
    currentPhase !== TURN_PHASES.ACompleting &&
    currentPhase !== TURN_PHASES.BCompleting;

  return (
    <div className="phrase-section-base phrase-section-completion">
      {shouldShowSmartCountdown && (
        <SmartCompletionCountdown
          allPrefixedWordsCorrect={allPrefixedWordsCorrect}
          spokenContinuation={spokenContinuation}
          continuationProgress={continuationProgress}
          onSendAction={handleAutoComplete}
        />
      )}
      <motion.div
        animate={shouldJump ? { y: [0, -5, 0] } : { y: 0 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <ParticipantCamera participant={participant} />
      </motion.div>
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
        <div
          className="complete-phrase"
          style={{
            fontSize: 36,
            textAlign: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2px',
            alignItems: 'center',
            color: 'white'
          }}
        >
          <PlayPhraseButton onClick={playFullPhrase} />
          <ToCompleteWordsContainer blocks={blocksWithStatus} />
          {isMyTurn ? (
            <>
              {/* <SmartCompletionCountdown /> */}
              <SpokenContinuationContainer
                continuation={spokenContinuation}
                allPrefixedWordsCorrect={allPrefixedWordsCorrect}
                minWords={MIN_CONTINUATION_WORDS}
              />
              <span style={{
                fontSize: '32px',
                fontWeight: 'normal',
                color: 'rgba(255, 255, 255, 0.9)',
                marginLeft: '8px'
              }}>
                .
              </span>
            </>
          ) : (
            <span style={{
              fontSize: '32px',
              fontWeight: 'normal',
              color: 'rgba(255, 255, 255, 0.9)',
              marginLeft: '8px'
            }}>
              ...
            </span>
          )}
        </div>
      </div>
    </div>
  );
}


